// src/hooks/order/useRecogeEnBodega.ts
// Hook para elección de bodega + "Cliente recoge en bodega"

import { useState, useEffect, useCallback } from 'react'
import type { OrderData } from '../../types/orderFormTypes'

/** Valor interno del select; ecommerce → API cadena vacía; 97 = En tránsito; 93/94 = ML */
export type BodegaRecogidaSeleccion = '' | '95' | '93' | '94' | 'ecommerce' | '97'
export type RecogeRespuesta = '' | 'si' | 'no'

/** Bodegas con misma dirección/stock que Xerox */
export const BODEGA_IDS_TIPO_XEROX: readonly BodegaRecogidaSeleccion[] = ['95', '93', '94']

/** ML fullfillment (93) y ML Distrito Cafetero (94): recoge en bodega siempre No */
export const BODEGA_IDS_SIN_RECOGE: readonly BodegaRecogidaSeleccion[] = ['93', '94']

export function isBodegaTipoXerox(sel: string): boolean {
  return BODEGA_IDS_TIPO_XEROX.includes(sel as BodegaRecogidaSeleccion)
}

export function isBodegaSinRecoge(sel: string): boolean {
  return BODEGA_IDS_SIN_RECOGE.includes(sel as BodegaRecogidaSeleccion)
}

function getMetaValue(initialData: OrderData | undefined, key: string): string | undefined {
  if (!initialData?.meta_data) return undefined
  const md: unknown = initialData.meta_data
  if (Array.isArray(md)) {
    const item = (md as Array<{ key: string; value: unknown }>).find((m) => m.key === key)
    if (item == null || item.value === undefined || item.value === null) return undefined
    return String(item.value)
  }
  if (md && typeof md === 'object' && !Array.isArray(md)) {
    const v = (md as Record<string, unknown>)[key]
    if (v === undefined || v === null) return undefined
    return String(v)
  }
  return undefined
}

/** `id_market` en la raíz del JSON del pedido (p. ej. GET /list/:id) */
function getRootIdMarket(initialData: OrderData | undefined): string | undefined {
  if (!initialData) return undefined
  if (!Object.prototype.hasOwnProperty.call(initialData, 'id_market')) return undefined
  const v = (initialData as Record<string, unknown>).id_market
  if (v === undefined || v === null) return undefined
  return String(v)
}

function hasRecogeFlag(initialData: OrderData | undefined): boolean {
  if (!initialData) return false
  return (
    initialData.meta_data?.recoge_en_bodega === 1 ||
    initialData.extraDetails?.recoge_en_bodega === 1 ||
    String(getMetaValue(initialData, 'recoge_en_bodega') || '') === '1'
  )
}

function inferTieneBodega(initialData: OrderData | undefined): boolean {
  if (!initialData) return false
  if (hasRecogeFlag(initialData)) return true
  const root = getRootIdMarket(initialData)
  if (root === undefined) return false
  const s = root.trim()
  return (
    s === '95' ||
    s === '93' ||
    s === '94' ||
    s === '97' ||
    s === '96' ||
    s === 'ecommerce' ||
    s === ''
  )
}

function seleccionFromInitial(
  initialData: OrderData | undefined,
  mode: 'view' | 'edit' | 'create'
): BodegaRecogidaSeleccion {
  if ((mode !== 'edit' && mode !== 'view') || !initialData) return ''
  const raw =
    getRootIdMarket(initialData) ??
    getMetaValue(initialData, 'id_market') ??
    getMetaValue(initialData, 'bodega_recogida_id') ??
    ''
  const normalized = String(raw).trim()
  if (normalized === '95') return '95'
  if (normalized === '93') return '93'
  if (normalized === '94') return '94'
  if (normalized === '97' || normalized === '96') return '97'
  if (normalized === 'ecommerce') return 'ecommerce'
  if (!inferTieneBodega(initialData) && !hasRecogeFlag(initialData)) return ''
  if (normalized === '' && hasRecogeFlag(initialData)) return 'ecommerce'
  // Si hay id_market vacío con flag o sin flag pero root vacío → ecommerce
  if (hasRecogeFlag(initialData) || inferTieneBodega(initialData)) {
    if (normalized === '') return 'ecommerce'
  }
  return normalized ? 'ecommerce' : ''
}

function recogeRespuestaFromInitial(
  initialData: OrderData | undefined,
  mode: 'view' | 'edit' | 'create',
  bodega: BodegaRecogidaSeleccion
): RecogeRespuesta {
  if ((mode !== 'edit' && mode !== 'view') || !initialData || !bodega) return ''
  if (isBodegaSinRecoge(bodega)) return 'no'
  return hasRecogeFlag(initialData) ? 'si' : 'no'
}

function getFechaEntregaFromInitial(initialData: OrderData | undefined): string {
  if (!initialData) return ''
  const fromMeta = getMetaValue(initialData, 'fecha_entrega')
  if (fromMeta) return fromMeta.slice(0, 10)
  const root = (initialData as Record<string, unknown>).fecha_entrega
  if (root != null && String(root).trim() !== '') return String(root).slice(0, 10)
  return ''
}

interface UseRecogeEnBodegaProps {
  initialData?: OrderData
  mode: 'view' | 'edit' | 'create'
  setFormData: React.Dispatch<React.SetStateAction<OrderData>>
}

interface UseRecogeEnBodegaReturn {
  /** Hay bodega seleccionada (equivalente al antiguo switch "Elige bodega") */
  eligeBodega: boolean
  recogeEnBodega: boolean
  recogeRespuesta: RecogeRespuesta
  handleRecogeRespuestaChange: (value: RecogeRespuesta) => void
  bodegaRecogidaSeleccion: BodegaRecogidaSeleccion
  handleBodegaRecogidaSeleccionChange: (value: BodegaRecogidaSeleccion) => void
  fechaEntregaActiva: boolean
  handleFechaEntregaActivaChange: (checked: boolean) => void
  fechaEntrega: string
  handleFechaEntregaChange: (value: string) => void
}

/** Xerox / ML fullfillment / ML Distrito Cafetero (id_market 95, 93, 94) */
const BODEGA_XEROX_ADDRESS = {
  address_1: 'Carrera 43A #15 sur 15, Medellin, Antioquia',
  address_2: '',
  state: '05',
  city: '05001'
}

/** Ecommerce y En tránsito (97) comparten dirección */
const BODEGA_ECOMMERCE_ADDRESS = {
  address_1: 'Calle 25 A # 43B - 199, Medellin, Antioquia',
  address_2: '',
  state: '05',
  city: '05001'
}

function patchDireccionRecogida(sel: BodegaRecogidaSeleccion) {
  if (isBodegaTipoXerox(sel)) return BODEGA_XEROX_ADDRESS
  if (sel === 'ecommerce' || sel === '97') return BODEGA_ECOMMERCE_ADDRESS
  return {
    address_1: '',
    address_2: '',
    state: '',
    city: ''
  }
}

function clearDireccionesYTransportadora(
  setFormData: React.Dispatch<React.SetStateAction<OrderData>>
) {
  setFormData((prev) => ({
    ...prev,
    billingAddress: {
      ...prev.billingAddress,
      address_1: '',
      address_2: '',
      state: '',
      city: ''
    },
    shippingAddress: {
      ...prev.shippingAddress,
      address_1: '',
      address_2: '',
      state: '',
      city: ''
    },
    extraDetails: {
      ...prev.extraDetails,
      transportadora: ''
    }
  }))
}

export function useRecogeEnBodega({
  initialData,
  mode,
  setFormData
}: UseRecogeEnBodegaProps): UseRecogeEnBodegaReturn {
  const [bodegaRecogidaSeleccion, setBodegaRecogidaSeleccion] = useState<BodegaRecogidaSeleccion>(() =>
    seleccionFromInitial(initialData, mode)
  )

  const [recogeRespuesta, setRecogeRespuesta] = useState<RecogeRespuesta>(() =>
    recogeRespuestaFromInitial(initialData, mode, seleccionFromInitial(initialData, mode))
  )

  const initialFecha = getFechaEntregaFromInitial(initialData)
  const [fechaEntregaActiva, setFechaEntregaActiva] = useState<boolean>(() => !!initialFecha)
  const [fechaEntrega, setFechaEntrega] = useState<string>(() => initialFecha)

  const recogeEnBodega = recogeRespuesta === 'si'
  const eligeBodega = bodegaRecogidaSeleccion !== ''

  useEffect(() => {
    if ((mode === 'edit' || mode === 'view') && initialData) {
      const bodega = seleccionFromInitial(initialData, mode)
      setBodegaRecogidaSeleccion(bodega)
      setRecogeRespuesta(recogeRespuestaFromInitial(initialData, mode, bodega))
      const fecha = getFechaEntregaFromInitial(initialData)
      setFechaEntrega(fecha)
      setFechaEntregaActiva(!!fecha)
    }
  }, [mode, initialData])

  // Direcciones solo cuando hay bodega + Cliente recoge = Sí
  useEffect(() => {
    if (!eligeBodega || !recogeEnBodega || !bodegaRecogidaSeleccion) return
    const patch = patchDireccionRecogida(bodegaRecogidaSeleccion)
    setFormData((prev) => ({
      ...prev,
      billingAddress: {
        ...prev.billingAddress,
        address_1: patch.address_1,
        address_2: patch.address_2,
        state: patch.state,
        city: patch.city
      },
      shippingAddress: {
        ...prev.shippingAddress,
        address_1: patch.address_1,
        address_2: patch.address_2,
        state: patch.state,
        city: patch.city
      }
    }))
  }, [eligeBodega, recogeEnBodega, bodegaRecogidaSeleccion, setFormData])

  const handleBodegaRecogidaSeleccionChange = useCallback((value: BodegaRecogidaSeleccion) => {
    setBodegaRecogidaSeleccion(value)
    if (!value) {
      setRecogeRespuesta('')
      clearDireccionesYTransportadora(setFormData)
      return
    }
    if (isBodegaSinRecoge(value)) {
      setRecogeRespuesta('no')
      clearDireccionesYTransportadora(setFormData)
    }
  }, [setFormData])

  const handleRecogeRespuestaChange = useCallback((value: RecogeRespuesta) => {
    if (isBodegaSinRecoge(bodegaRecogidaSeleccion)) {
      setRecogeRespuesta('no')
      clearDireccionesYTransportadora(setFormData)
      return
    }
    setRecogeRespuesta(value)
    if (value === 'si') {
      setFormData((prev) => ({
        ...prev,
        extraDetails: {
          ...prev.extraDetails,
          transportadora: 'Entrega PDV'
        }
      }))
    } else {
      clearDireccionesYTransportadora(setFormData)
    }
  }, [bodegaRecogidaSeleccion, setFormData])

  const handleFechaEntregaActivaChange = useCallback((checked: boolean) => {
    setFechaEntregaActiva(checked)
    if (!checked) {
      setFechaEntrega('')
    }
  }, [])

  const handleFechaEntregaChange = useCallback((value: string) => {
    setFechaEntrega(value)
  }, [])

  return {
    eligeBodega,
    recogeEnBodega,
    recogeRespuesta,
    handleRecogeRespuestaChange,
    bodegaRecogidaSeleccion,
    handleBodegaRecogidaSeleccionChange,
    fechaEntregaActiva,
    handleFechaEntregaActivaChange,
    fechaEntrega,
    handleFechaEntregaChange
  }
}
